import { extractNumberFromString } from "helpers/toolsHelper";

const GEOGRCOV_TO_COVERAGE_CODE = {
    laopen: 'latinAmericaDefault',
    lalted: 'latinAmericaDefault',
    latam: 'latinAmericaDefault',
    wwltd: 'worldwideLimited',
    wwide: 'worldwideDefault',
    glopen: 'worldwideDefault',
    gllted: 'worldwideDefault',
};

const ZERO_ID_PATTERN = /(?:^|_)0(?:\.0+)?$/;

export const isLegacyQuotedContext = (contextState = {}) => Boolean(
    contextState?.hasLoadedQuoteContext && !contextState?.isCountryResidenceDeductibleFlagPresent
);

export const shouldUseDynamicDeductibleByCountry = (contextState = {}) => !isLegacyQuotedContext(contextState);

export const resolveCountryResidenceDeductibleFlag = ({
    countryValue,
    deductibleCountries,
    contextState,
}) => {
    if (!countryValue || !shouldUseDynamicDeductibleByCountry(contextState)) {
        return false;
    }

    if (!Array.isArray(deductibleCountries) || deductibleCountries.length === 0) {
        return false;
    }

    return deductibleCountries.some(country => String(country?.isoNum) === String(countryValue));
};

export const shouldApplyDedusa = (selectedBenefitsIds = {}, isCountryResidenceDeductible = false) => {
    const geo = selectedBenefitsIds?.geogrcov;
    return isCountryResidenceDeductible || (geo !== 'wwltd' && geo !== 'latam');
};

export const isZeroDeductibleOption = ({ id, description }) => {
    const hasZeroId = ZERO_ID_PATTERN.test(String(id ?? '').toLowerCase());
    if (hasZeroId) {
        return true;
    }

    const value = extractNumberFromString(description);
    return Number.isFinite(value) && value === 0;
};

export const hasSelectedZeroDeductibleInsideCountry = (contextState = {}) => {
    return isZeroDeductibleOption({
        id: contextState?.selectedBenefitsIds?.dedusa,
        description: contextState?.selectedBenefitsNames?.dedusa,
    });
};

const getOptionIds = (allBenefits, benefitId) =>
    allBenefits.filter(b => b['factorId.id'] === benefitId).map(b => b.id);

export const getDeductibleIgnoredOptions = (countryConfig, geogrcovCode, selectedBenefitsIds, allBenefits, isCountryResidenceDeductible) => {
    const ignored = [];

    if (!countryConfig) return ignored;

    // Colombia uses a single coverageCode for all geographic coverage types.
    // All other countries map the selected geogrcov option to a specific coverageCode.
    // When no geographic coverage has been selected yet, fall back to the worldwide
    // default config so the deductible lists are filtered instead of showing every option.
    const coverageCode = isCountryResidenceDeductible
        ? 'default'
        : (GEOGRCOV_TO_COVERAGE_CODE[geogrcovCode] ?? 'worldwideDefault');

    if (!coverageCode) return ignored;

    const coverageEntry = countryConfig.geographicalCoverage.find(gc => gc.coverageCode === coverageCode)
        ?? countryConfig.geographicalCoverage[0];
    if (!coverageEntry) return ignored;

    const { order, primary } = coverageEntry;
    const isPrimaryInside = order === 'inside';
    const primaryBenefitId = isPrimaryInside ? 'dedusa' : 'dednonusa';
    const dependentBenefitId = isPrimaryInside ? 'dednonusa' : 'dedusa';

    const availablePrimary = new Set(primary.flatMap(p => p.options.map(o => o.code)));
    const allPrimary = getOptionIds(allBenefits, primaryBenefitId);
    ignored.push(...allPrimary.filter(id => !availablePrimary.has(id)));

    // Filter the dependent list too. When a primary option is selected, narrow it to that
    // option's valid combinations. Before any primary is selected, mount it with the union
    // of every valid combination across all primary groups (a sensible default) instead of
    // showing every raw option.
    const allDependent = getOptionIds(allBenefits, dependentBenefitId);
    const selectedPrimary = selectedBenefitsIds && selectedBenefitsIds[primaryBenefitId];
    const selectedGroup = selectedPrimary
        ? primary.find(p => p.options.some(o => o.code === selectedPrimary))
        : null;
    const relevantGroups = selectedGroup ? [selectedGroup] : primary;

    // 'n/a' means the dependent deductible does not apply for that primary option.
    const dependentApplies = relevantGroups.some(g => !g.combinations.some(c => c.code === 'n/a'));
    if (!dependentApplies) {
        ignored.push(...allDependent);
    } else {
        const validDependent = new Set(
            relevantGroups
                .flatMap(g => g.combinations)
                .map(c => c.code)
                .filter(code => code !== 'n/a')
        );
        ignored.push(...allDependent.filter(id => !validDependent.has(id)));
    }

    return ignored;
};

export const removeDedusaFromContextSelections = (contextState = {}) => {
    const { dedusa: _removedBenefit, ...selectedBenefits } = contextState.selectedBenefits || {};
    const { dedusa: _removedBenefitName, ...selectedBenefitsNames } = contextState.selectedBenefitsNames || {};
    const { dedusa: _removedBenefitId, ...selectedBenefitsIds } = contextState.selectedBenefitsIds || {};

    return {
        ...contextState,
        selectedBenefits,
        selectedBenefitsNames,
        selectedBenefitsIds,
    };
};
